import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { addCandidateDocuments, completeCandidateDocumentsVerification, deleteCandidateDocument, deleteEmploymentHistoryDocument, downloadAllCandidateDocumentsZip, downloadCandidateDocument, getCandidateDocuments, submitCandidateDocuments, verifyCandidateDocument } from '../candidatePreboardingApi';
import { useCandidatePreboarding } from '../CandidatePreboardingContext';
import CandidateDocumentsTable from './CandidateDocumentsTable';
import { theme } from '../../../../../theme';
import { useSnackbar } from '../../../../../Context/SnackbarContext';
import { useGlobalContext } from '../../../../../Context/GlobalContext';
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import ReUploadNotify from './ReUploadNotify';
import { getEmailTemplates } from '../../../Admin/Communication/communicationApi';
import { emailTemplate } from '../../../CandidateManagement/CandidateContent/CandidateContent';
import { removeSpacing, sanitizeContent } from '../../../../Common/Sanitize/sanitizeReactQuill';
import { sendPreboardingNotification } from '../../preboardingApi';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { useLoadingBackdrop } from '../../../../../Context/LoadingBackdropContext';
import DocumentPreview from './DocumentPreview';

export interface DocumentsType {
  customFieldID: number;
  fieldName: string;
  isMandatory: boolean;
  sectionId: number;
  sectionName: string;
  isUploaded: boolean;
  fileName?: string;
  s3Path?: string;
  mimeType?: string;
  isVerified?: boolean;
  verifiedBy?: string | null;
  verifiedDate?: string | null;
  createdBy?: number;
  createdDate?: string;
  id?: number; // DocumentId
  isReuploadRequested?: boolean;
}


export interface sectionType {
  sectionId: number;
  sectionName: string;
}

export interface employmentHistoryType {
  employmentId: number;
  mapId: number;
  companyName: string;
  fileName?: string;
  s3Path?: string;
  mimeType?: string;
  isVerified?: boolean;
  verifiedBy?: string | null;
  verifiedDate?: string | null;
  createdBy?: number;
  createdDate?: string;
  id?: number;
  isUploaded?: boolean;
  isReuploadRequested?: boolean;
}

const Documents: React.FC = () => {
  const { tenant } = useParams<{ tenant: string }>();
  const { candidateData, fetchCandidateData } = useCandidatePreboarding();
  const isCandidate = !!sessionStorage.getItem("candidate_session");
  const [documents, setDocuments] = useState<DocumentsType[]>([]);
  const [sections, setSections] = useState<sectionType[]>([]);
  const [loading, setLoading] = useState(true);
  const { showSnackbar } = useSnackbar();
  const [hasExperience, setHasExperience] = useState<boolean | null>(null);
  const [employmentHistory, setEmploymentHistory] = useState<employmentHistoryType[]>([]);
  const { userData } = useGlobalContext();
  const navigate = useNavigate();
  const [uploadOnBehalf, setUploadOnBehalf] = useState(false);
  const [hasDocuments, setHasDocuments] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isAllVerified, setIsAllVerified] = useState(false);
  const [isOpenReuploadNotify, setIsOpenReuploadNotify] = useState(false);
  const [emailData, setEmailData] = useState<emailTemplate[]>([])
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailNum, setEmailNum] = useState<number | null>(null);
  const [hasRequestedReupload, setHasRequestedReupload] = useState(false);
  const { show, hide } = useLoadingBackdrop();
  const [openDocumentPreview, setOpenDocumentPreview] = useState(false);
  const [documentData, setDocumentData] = useState<{ url: string; mimeType: string }>({ url: "", mimeType: "" });


  // Refs for file inputs
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // State for uploads (all use string keys now)
  const [uploadedFiles, setUploadedFiles] = useState<{ [key: string]: boolean }>({});
  const [selectedFiles, setSelectedFiles] = useState<{ [key: string]: File | null }>({});

  // Employment history upload states (also string keys)
  const [employmentUploadedFiles, setEmploymentUploadedFiles] = useState<{ [key: string]: boolean }>({});
  const [employmentSelectedFiles, setEmploymentSelectedFiles] = useState<{ [key: string]: File | null }>({});


  // Fetch candidate documents
  const fetchCandidateDocuments = async () => {
    try {
      const payLoad = {
        MapID: candidateData?.mapID
      };
      setLoading(true);
      const result = await getCandidateDocuments(tenant, payLoad, isCandidate);

      if (result) {
        // Map candidate documents
        const mappedResult = result.Documents.map((doc: any) => ({
          customFieldID: doc.CustomFieldId,
          fieldName: doc.FieldName,
          isMandatory: doc.IsMandatory,
          sectionId: doc.SectionId,
          sectionName: doc.SectionName,
          isUploaded: doc.IsUploaded,
          fileName: doc.FileName,
          s3Path: doc.S3Path,
          mimeType: doc.Mimetype,
          isVerified: doc.IsVerified,
          verifiedBy: doc.VerifiedBy,
          verifiedDate: doc.VerifiedDate,
          createdBy: doc.CreatedBy,
          createdDate: doc.CreatedDate,
          id: doc.Id, // DocumentId
          isReuploadRequested: doc.IsReuploadRequested
        }));

        setDocuments(mappedResult);

        // 🔹 Pre-populate uploaded files state (with prefixes)
        const initialUploaded: { [key: string]: boolean } = {};
        const initialSelected: { [key: string]: File | null } = {};

        result.Documents.forEach((doc: any) => {
          const key = `doc-${doc.CustomFieldId}`;
          if (doc.IsUploaded && doc.S3Path) {
            initialUploaded[key] = true;
            initialSelected[key] = null;
          }
        });

        setUploadedFiles(initialUploaded);
        setSelectedFiles(initialSelected);

        // Map sections
        const mappedSections = result.Sections.map((section: any) => ({
          sectionId: section.SectionId,
          sectionName: section.SectionName
        }));

        // Map employment history
        const mappedEmploymentHistory = result.EmploymentHistory.map((emp: any) => ({
          employmentId: emp.EmploymentId,
          mapId: emp.MapId,
          companyName: emp.OrganizationName,
          fileName: emp.FileName,
          s3Path: emp.S3Path,
          mimeType: emp.Mimetype,
          isVerified: emp.IsVerified,
          verifiedBy: emp.VerifiedBy,
          verifiedDate: emp.VerifiedDate,
          createdBy: emp.CreatedBy,
          createdDate: emp.CreatedDate,
          id: emp.Id, // DocumentId
          isUploaded: emp.IsUploaded,
          isReuploadRequested: emp.IsReuploadRequested
        }));
        setEmploymentHistory(mappedEmploymentHistory);

        // Check if any documents exist or reupload requested
        setHasDocuments(
          mappedResult.some((doc: DocumentsType) => doc.s3Path) ||
          mappedEmploymentHistory.some((emp: employmentHistoryType) => emp.s3Path)
        );
        setHasRequestedReupload(
          mappedResult.some((doc: DocumentsType) => doc.isReuploadRequested) ||
          mappedEmploymentHistory.some((emp: employmentHistoryType) => emp.isReuploadRequested)
        );

        // 🔹 Employment uploaded docs (with prefixes)
        const initialEmpUploaded: { [key: string]: boolean } = {};
        const initialEmpSelected: { [key: string]: File | null } = {};

        result.EmploymentHistory.forEach((emp: any) => {
          const key = `emp-${emp.EmploymentId}`;
          if (emp.IsUploaded && emp.S3Path) {
            initialEmpUploaded[key] = true;
            initialEmpSelected[key] = null;
          }
        });

        setEmploymentUploadedFiles(initialEmpUploaded);
        setEmploymentSelectedFiles(initialEmpSelected);

        setHasExperience(result.HasExperience);
        setSections(mappedSections);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (candidateData?.mapID) {
      fetchCandidateDocuments();
    }

  }, [tenant, candidateData?.mapID])

  const fetchEmailTemplates = async () => {
    const result = await getEmailTemplates(tenant)
    setEmailData(result)
  }

  useEffect(() => {
    if (!isCandidate) {
      fetchEmailTemplates()
    }
  }, [tenant]);


  // Place this function inside your Documents component (before the return statement)

  const getUnverifiedDocsList = (unVerifiedAllDocs: any[]) => {
    return unVerifiedAllDocs
      .map((doc, idx) =>
        doc.fieldName
          ? `${idx + 1}. ${doc.fieldName}`
          : doc.companyName
            ? `${idx + 1}. Experience Letter - ${doc.companyName}`
            : `${idx + 1}. Document`
      )
      .join('<br/>');
  };

  // Add this logic where you handle the "Request Re-upload" action

  const handleRequestReupload = () => {
    const unVerifiedAllDocs = [
      // Always include mandatory, unverified docs except "Professional Experience Documents"
      ...documents.filter(
        doc =>
          // doc.isMandatory &&
          !doc.isVerified &&
          doc.s3Path &&
          (doc.sectionName !== "Professional Experience Documents" || hasExperience)
      ),
      // Include unverified employment history only if hasExperience is true
      ...(hasExperience ? employmentHistory.filter(emp => !emp.isVerified && emp.s3Path) : [])
    ];


    const docsList = getUnverifiedDocsList(unVerifiedAllDocs);

    // Find the template and replace the placeholder
    const emailTemplate = emailData.find(t => t.EmailKey.toLowerCase() === 'request to re-upload documents')

    const emailBody = emailTemplate?.EmailBody.replace('@UnverifiedDocsList', docsList).replace('@CandidateName', candidateData?.candidateName || '').replace('@Recruiter', candidateData?.recruiterName || '').replace("@JobTitle", candidateData?.jobTitle || '');

    setEmailSubject(emailTemplate?.EmailSubject || "");
    setEmailBody(emailBody || "");
    setEmailNum(emailTemplate?.EmailNum || null);
    setIsOpenReuploadNotify(true);
  };

  const handleSendEmailNotification = async () => {
    if (!emailSubject || !emailBody || !candidateData?.candidateName || !candidateData.candidateEmail || !candidateData?.jobNum || !candidateData?.mapID) {
      showSnackbar("Please fill in all required fields.", "error");
      return;
    }

    const sanitizedValue = sanitizeContent(emailBody)
    if (!sanitizedValue) {
      showSnackbar("Email body  cannot be empty.", "error");
      return;
    }

    if (!emailSubject.trim()) {
      showSnackbar("Subject cannot be empty.", "error")
      return
    }

    const unVerifiedCandidateDocs = documents.filter(doc =>
      doc.isMandatory &&
      !doc.isVerified &&
      (doc.sectionName !== "Professional Experience Documents" || hasExperience));
    const unVerifiedEmploymentDocs = hasExperience ? employmentHistory.filter(emp => !emp.isVerified) : [];

    try {
      show();
      const payLoad = {
        "EmailSubject": emailSubject,
        "EmailBody": removeSpacing(emailBody),
        "CandidateName": candidateData?.candidateName || "",
        "CandidateNum": candidateData?.candidateNum || null,
        "JobNum": candidateData?.jobNum || null,
        "MapID": candidateData?.mapID || null,
        "EmailID": candidateData?.candidateEmail || "",
        "EmailTemplateName": "Request to Re-upload Documents",
        "EmailNum": emailNum,
        "UnverifiedCandidateDocs": unVerifiedCandidateDocs.map((doc) => doc.id) || [],
        "UnverifiedEmploymentDocs": unVerifiedEmploymentDocs.map((doc) => doc.id) || []
      }
      const result = await sendPreboardingNotification(tenant, payLoad)
      if (result) {
        showSnackbar("Notification sent successfully to the candidate.", "success");
        setIsOpenReuploadNotify(false);
        if (result.Status) {
          fetchCandidateDocuments()
        }
      }
    } catch (error) {
      showSnackbar("Failed to send notification to the candidate.", "error");
      setIsOpenReuploadNotify(false);
      console.log(error)
    }finally{
      hide();
    }

    // Send email notification logic here
  }


  const handleDeleteFile = (customFieldID: number) => {
    const key = `doc-${customFieldID}`; // add prefix

    setUploadedFiles((prev) => ({ ...prev, [key]: false }));
    setSelectedFiles((prev) => ({ ...prev, [key]: null }));

    if (fileInputRefs.current[key]) {
      fileInputRefs.current[key]!.value = "";
    }
  };


  const handleConfirmDelete = async (documentId: number) => {
    if (!documentId) return;

    try {
      const payLoad = {
        DocumentId: documentId,
      }

      const response = await deleteCandidateDocument(tenant, payLoad, isCandidate);
      if (response) {
        showSnackbar(response.Message, response.Status ? "success" : "error");
        if (response.Status) {
          fetchCandidateDocuments();
        }
      }
    } catch (error) {
      console.log(error)
    }

  }

  const handleAttachClick = (customFieldID: number) => {
    const key = `doc-${customFieldID}`; // add prefix
    fileInputRefs.current[key]?.click();
  };


  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, doc: DocumentsType) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        showSnackbar("File size should not exceed 2MB.", "error");
        return;
      }

      const key = `doc-${doc.customFieldID}`; // add prefix

      setUploadedFiles((prev) => ({ ...prev, [key]: true }));
      setSelectedFiles((prev) => ({ ...prev, [key]: file }));
    }
  };




  const handleEmploymentDeleteFile = (employmentId: number) => {
    const key = `emp-${employmentId}`; // add prefix

    setEmploymentUploadedFiles((prev) => ({ ...prev, [key]: false }));
    setEmploymentSelectedFiles((prev) => ({ ...prev, [key]: null }));

    if (fileInputRefs.current[key]) {
      fileInputRefs.current[key]!.value = "";
    }
  };


  // For confirming deletion of employment history document
  const handleConfirmDeleteEmploymentFile = async (documentId: number) => {
    if (!documentId) return;

    try {
      const payLoad = {
        DocumentId: documentId,
      }

      const response = await deleteEmploymentHistoryDocument(tenant, payLoad, isCandidate);
      if (response) {
        showSnackbar(response.Message, response.Status ? "success" : "error");
        if (response.Status) {
          fetchCandidateDocuments();
        }
      }
    } catch (error) {
      console.log(error)
    }

  }

  const handleEmploymentAttachClick = (employmentId: number) => {
    const key = `emp-${employmentId}`; // add prefix
    fileInputRefs.current[key]?.click();
  };


  const handleEmploymentFileChange = (event: React.ChangeEvent<HTMLInputElement>, employmentId: number) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        showSnackbar("File size should not exceed 2MB.", "error");
        return;
      }

      const key = `emp-${employmentId}`; // add prefix

      setEmploymentUploadedFiles((prev) => ({ ...prev, [key]: true }));
      setEmploymentSelectedFiles((prev) => ({ ...prev, [key]: file }));
    }
  };


  // ...existing code...
  const validateMandatoryDocs = () => {
    const missingMandatoryDocs = documents.filter(
      doc =>
        doc.isMandatory &&
        !doc.s3Path &&
        (
          doc.sectionName !== "Professional Experience Documents" ||
          hasExperience
        )
    );

    const employmentMissingMandatoryDocs = employmentHistory.filter(
      emp => !emp.s3Path
    );

    return missingMandatoryDocs.length === 0 && employmentMissingMandatoryDocs.length === 0;
  }

  // For submitting all documents
  const handleSubmit = async () => {

    if (!validateMandatoryDocs()) {
      showSnackbar("Please upload all mandatory documents.", "error");
      return;
    }

    if (hasRequestedReupload) {
      showSnackbar("You have pending re-upload requests. Please resolve them before submitting.", "error");
      return;
    }


    const payLoad = {
      MapID: candidateData?.mapID || 0,
      CreatedBy: userData?.UserNum || 0,
      PreboardingStatusKey: candidateData?.preboardingStatusKey || "",
      IsCandidate: isCandidate ? true : false,
      RecruiterName: candidateData?.recruiterName || "",
      RecruiterEmail: candidateData?.recruiterEmail || "",
      UserId: userData?.UserID || ""
    }
    try {
      show();
      const response = await submitCandidateDocuments(tenant, payLoad, isCandidate);
      if (response) {
        showSnackbar(response.Message, response.Status ? "success" : "error");
        if (isCandidate) {
          navigate(`/${tenant}/ThankYou`, { state: { message: "Thank you for submitting your details and documents. Our team will review your information and reach out if we need any additional clarification." } })
          sessionStorage.removeItem("candidate_session");
        } else {
          setUploadOnBehalf(false);
          fetchCandidateDocuments();
          fetchCandidateData();
        }

      }
    } catch (error) {
      showSnackbar("Failed to submit documents. Please try again.", "error");
      console.log(error)
    } finally {
      hide();
    }
  };

  // For downloading individual document
  const handleDownloadDocument = async (documentId: number, documentType: string, isPreview: boolean = false) => {
    if (!documentId || !documentType) {
      showSnackbar("Invalid document details.", "error");
      return;
    };
    const payLoad = {
      DocumentId: documentId,
      DocumentType: documentType
    }
    show();
    try {
      const response = await downloadCandidateDocument(tenant, payLoad, isCandidate);

      if (!response) {
        showSnackbar("No response received while downloading document.", "error");
        return;
      }

      const allowedTypes = [
        'application/pdf',
        'image/png',
        'image/jpeg',
        'image/jpg'
      ];

      if (!allowedTypes.includes(response.data.type)) {
        // Assume error blob
        const text = await response.data.text();
        let errorData;
        try {
          errorData = JSON.parse(text);
        } catch {
          errorData = { Message: "Failed to download document" };
        }
        showSnackbar(errorData.Message || "Failed to download document", "error");
        return;
      }

      const blob = response.data;

      // Try to extract filename from headers
      const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let filename = 'resume.pdf'; // fallback

      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match?.[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);

      if (isPreview) {
        setOpenDocumentPreview(true);
        setDocumentData({ url, mimeType: response.data.type });
        
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      showSnackbar("Failed to download document. Please try again.", "error");
      console.log(error)
    }finally{
      hide();
    }
  }

  // For downloading all documents as ZIP
  const handleDownloadAllDocuments = async (mapId: number, candidateName: string) => {

    if (!mapId) {
      showSnackbar("Missing MapID. Cannot download documents.", "error");
      return;
    }
    try {
      setIsDownloading(true);
      const payLoad = {
        MapID: mapId,
        CandidateName: candidateName
      }

      const response = await downloadAllCandidateDocumentsZip(tenant, payLoad);
      if (!response) {
        showSnackbar("No response received while downloading all documents.", "error");
        return;
      }

      if (response.data.type !== 'application/zip') {
        // Assume error blob
        const text = await response.data.text();
        let errorData;
        try {
          errorData = JSON.parse(text);
        } catch {
          errorData = { Message: "Failed to download all documents." }
        }
        showSnackbar(errorData.Message || "Failed to download all documents.", "error");
        return;
      }

      const blob = response.data;

      // Try to extract filename from headers
      const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let filename = 'All_Documents.zip'; // fallback

      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match?.[1]) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showSnackbar("Failed to download all documents. Please try again.", "error");
      console.log(error);
    } finally {
      setIsDownloading(false);
    }
  }

  // For verifying document 
  const handleVerifyDocument = async (documentId: number, documentType: string, isVerified: boolean) => {
    const payLoad = {
      DocumentId: documentId,
      DocumentType: documentType,
      IsVerified: isVerified
    }

    const response = await verifyCandidateDocument(tenant, payLoad);
    if (response) {
      if (response.Status) {
        fetchCandidateDocuments();
      }
    }
  }

  const handleUploadDocs = async () => {
    try {
      show();
      const formData = new FormData();
      formData.append("MapID", candidateData?.mapID?.toString() || "");
      formData.append("CreatedBy", userData?.UserNum?.toString() || "");
      formData.append("UserId", userData?.UserID || "");

      // Candidate Documents: remove prefix for API
      Object.entries(selectedFiles).forEach(([key, file]) => {
        if (file) {
          const customFieldId = key.replace("doc-", ""); // remove prefix
          formData.append(`CandidateDocuments[${customFieldId}]`, file);
        }
      });

      // Employment History Docs: remove prefix for API
      Object.entries(employmentSelectedFiles).forEach(([key, file]) => {
        if (file) {
          const employmentId = key.replace("emp-", ""); // remove prefix
          formData.append(`EmploymentHistoryDocs[${employmentId}]`, file);
        }
      });

      const response = await addCandidateDocuments(tenant, formData, isCandidate);
      if (response) {
        if (response.Status) {
          // Reset states
          showSnackbar(response.Message, "success");
          setUploadedFiles({});
          setSelectedFiles({});
          setEmploymentUploadedFiles({});
          setEmploymentSelectedFiles({});
          fetchCandidateDocuments();
        } else {
          showSnackbar(response.Message, response.Status ? "success" : "error");
        }
      }
    } catch (error) {
      showSnackbar("Failed to upload documents. Please try again.", "error");
      console.log(error);
    } finally {
      hide();
    }
  };


  const handleCompleteVerification = async () => {
    try {
      show()
      const payload = {
        "MapID": candidateData?.mapID || 0,
        "PreboardingStatusKey": candidateData?.preboardingStatusKey || "",
      }

      const response = await completeCandidateDocumentsVerification(tenant, payload);
      if (response) {
        showSnackbar(response.Message, response.Status ? "success" : "error");
        if (response.Status) {
          fetchCandidateData();
          fetchCandidateDocuments();
        }
      }
    } catch (error) {
      showSnackbar("Failed to complete verification. Please try again.", "error");
      console.log(error)
    }finally{
      hide();
    }
  }

 const documentPreviewForNonUploaded = (id:number,docType:string)=>{
    let file
    if(docType === "Candidate Document"){
      file = selectedFiles[`doc-${id}`]
    }else{
      file = employmentSelectedFiles[`emp-${id}`]
    }

    if(file){
      const url = URL.createObjectURL(file);
      setDocumentData({ url, mimeType: file.type });
      setOpenDocumentPreview(true);
    }
 }




  useEffect(() => {
    const allCandidateDocsVerified = documents.every(
      doc =>
        !doc.isMandatory ||
        doc.isVerified ||
        (doc.sectionName === "Professional Experience Documents" && !hasExperience)
    );
    const allEmploymentDocsVerified = hasExperience
      ? employmentHistory.every(emp => emp.isVerified)
      : true;
    setIsAllVerified(allCandidateDocsVerified && allEmploymentDocsVerified);
  }, [documents, employmentHistory, hasExperience]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        width="100%"
      >
        <CircularProgress />
      </Box>
    )
  }

  // Helper: Render warning if offer not accepted
  const OfferNotAcceptedWarning = () => (
    candidateData?.isOfferAccepted === false && (
      <Box sx={{
        bgcolor: theme.palette.primary.light,
        width: '100%',
        mt: '1vw',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1
      }}>
        <WarningOutlinedIcon sx={{ color: theme.palette.warning.main }} />
        <Typography variant='subtitle1'>
          Action Restricted : This action will be enabled only after the candidate approves the offer letter.
        </Typography>
      </Box>
    )
  );

  // Helper: Render re-upload warning
  const ReuploadWarning = () => (
    hasRequestedReupload && (isCandidate || uploadOnBehalf) && (
      <Box sx={{
        bgcolor: theme.palette.primary.light,
        width: '100%',
        mt: '1vw',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1
      }}>
        <WarningAmberRoundedIcon fontSize='medium' sx={{ color: theme.palette.error.main, mr: 0.5 }} />
        <Typography variant='subtitle1'>
          Please re-upload the documents at your earliest convenience. The red icon indicates that the previously submitted files were inaccurate or unclear.
        </Typography>
      </Box>
    )
  );

  // Helper: Render submit/cancel/upload-on-behalf/verify/reupload buttons
  const ActionButtons = () => {
    // const canShowSubmit = (candidateData?.preboardingStatusKey !== "PendingDocumentVerification" &&
    //   candidateData?.preboardingStatusKey !== "BGVPending") || hasRequestedReupload;
    const canShowSubmit = true;

    if (isCandidate && canShowSubmit && ["PendingDocumentVerification", "DocumentsPending"].includes(candidateData?.preboardingStatusKey || "")) {
      return (
        <Box sx={{ position: 'absolute', right: 0, mt: '1.4vw', top: '-3.62vw' }}>
          <Button
            variant="contained"
            disabled={!validateMandatoryDocs()}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </Box>
      );
    }

    if (userData?.UserRole && uploadOnBehalf) {
      return (
        <Box sx={{ position: 'absolute', right: 0, mt: '1.4vw', top: '-3.62vw', display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={() => setUploadOnBehalf(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ ml: 2 }}
            disabled={!validateMandatoryDocs()}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </Box>
      );
    }

    if (userData?.UserRole && !uploadOnBehalf && ["PendingDocumentVerification", "DocumentsPending"].includes(candidateData?.preboardingStatusKey || "")) {
      return (
        <Box sx={{ position: 'absolute', right: 0, mt: '1.4vw', top: '-3.62vw', display: 'flex', gap: 2 }}>
          {canShowSubmit && (
            <Button
              variant="outlined"
              sx={{
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
              }}
              onClick={() => setUploadOnBehalf(true)}
              disabled={candidateData?.isOfferAccepted === false}
            >
              Upload Documents on behalf
            </Button>
          )}
          <Button
            variant="contained"
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: '#FFFFFF',
              ml: 2,
            }}
            disabled={!hasDocuments || candidateData?.isOfferAccepted === false}
            onClick={() => {
              if (isAllVerified) {
                handleCompleteVerification()
              } else {
                handleRequestReupload();
              }
            }}
          >
            {isAllVerified ? "Complete Verification" : "Request Re-upload"}
          </Button>
        </Box>
      );
    }

    return null;
  };

  // Helper: Render download/upload buttons in header
  const HeaderActions = () => {
    // const canShowUpload = (candidateData?.preboardingStatusKey !== "PendingDocumentVerification" &&
    //   candidateData?.preboardingStatusKey !== "BGVPending") || hasRequestedReupload;
    const canShowUpload = true;

    return (
      <>
        {userData?.UserRole && !uploadOnBehalf && (
          <Button
            variant="outlined"
            sx={{
              borderColor: theme.palette.text.secondary,
              color: theme.palette.text.primary,
              position: 'relative'
            }}
            startIcon={isDownloading ? <> </> : <DownloadIcon />}
            disabled={!hasDocuments || isDownloading}
            onClick={() => handleDownloadAllDocuments(candidateData?.mapID!, candidateData?.candidateName!)}
            title='Download All Documents as ZIP'
          >
            {isDownloading ? (
              <>
                <CircularProgress
                  size={20}
                  sx={{
                    color: theme.palette.primary.main,
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    marginTop: '-10px'
                  }}
                />
                <span style={{ marginLeft: 28 }}>Downloading...</span>
              </>
            ) : (
              "Download All"
            )}
          </Button>
        )}
        {(canShowUpload && ["PendingDocumentVerification", "DocumentsPending"].includes(candidateData?.preboardingStatusKey || "") && (isCandidate || uploadOnBehalf)) && (
          <Button
            variant='outlined'
            startIcon={<FileUploadOutlinedIcon />}
            title='Upload Documents'
            disabled={
              Object.values(selectedFiles).every(file => !file) &&
              Object.values(employmentSelectedFiles).every(file => !file)
            }
            onClick={handleUploadDocs}
          >
            Upload
          </Button>
        )}
      </>
    );
  };


  // Helper function to render a CandidateDocumentsTable for a section
  const renderDocumentTable = (section: sectionType, idx: number) => {
    // Professional Experience Documents section (index 4)
    if (section.sectionName === "Professional Experience Documents") {
      if (!hasExperience) return null;
      return (
        <CandidateDocumentsTable
          key={section.sectionId}
          documents={documents}
          section={section}
          fileInputRefs={fileInputRefs}
          uploadedFiles={uploadedFiles}
          selectedFiles={selectedFiles}
          handleDeleteFile={handleDeleteFile}
          handleAttachClick={handleAttachClick}
          handleFileChange={handleFileChange}
          employmentHistory={employmentHistory}
          employmentUploadedFiles={employmentUploadedFiles}
          employmentSelectedFiles={employmentSelectedFiles}
          handleEmploymentDeleteFile={handleEmploymentDeleteFile}
          handleEmploymentAttachClick={handleEmploymentAttachClick}
          handleEmploymentFileChange={handleEmploymentFileChange}
          handleConfirmDelete={handleConfirmDelete}
          handleConfirmDeleteEmploymentFile={handleConfirmDeleteEmploymentFile}
          uploadOnBehalf={uploadOnBehalf}
          isCandidate={isCandidate}
          handleDownloadDocument={handleDownloadDocument}
          handleVerifyDocument={handleVerifyDocument}
          documentPreviewForNonUploaded={documentPreviewForNonUploaded}
        />
      );
    }

    // All other sections
    return (
      <CandidateDocumentsTable
        key={section.sectionId}
        documents={documents}
        section={section}
        fileInputRefs={fileInputRefs}
        uploadedFiles={uploadedFiles}
        selectedFiles={selectedFiles}
        handleDeleteFile={handleDeleteFile}
        handleAttachClick={handleAttachClick}
        handleFileChange={handleFileChange}
        handleConfirmDelete={handleConfirmDelete}
        uploadOnBehalf={uploadOnBehalf}
        isCandidate={isCandidate}
        handleDownloadDocument={handleDownloadDocument}
        handleVerifyDocument={handleVerifyDocument}
        documentPreviewForNonUploaded={documentPreviewForNonUploaded}
      />
    );
  };



  return (
    <Box sx={{ width: '100%', position: 'relative' }}>

      <OfferNotAcceptedWarning />
      <ReuploadWarning />
      <ActionButtons />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          mt: 3
        }}
      >
        <Typography variant="h5">
          Identity Verification Documents
        </Typography>
        <HeaderActions />
      </Box>

      <Divider sx={{ my: 2 }} />

      {sections.map(renderDocumentTable)}

      <ReUploadNotify
        isOpen={isOpenReuploadNotify}
        onClose={() => setIsOpenReuploadNotify(false)}
        candidateName={candidateData?.candidateName || ''}
        candidateEmail={candidateData?.candidateEmail || ''}
        emailBody={emailBody}
        emailSubject={emailSubject}
        setEmailSubject={setEmailSubject}
        setEmailBody={setEmailBody}
        handleSendEmailNotification={handleSendEmailNotification}
      />

      <DocumentPreview
        open={openDocumentPreview}
        onClose={() => setOpenDocumentPreview(false)}
        url={documentData.url}
        mimeType={documentData.mimeType}
      />
    </Box>
  );
}

export default Documents